// src/components/CategoryProducts.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Package, ArrowRight } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  short_description: string;
  long_description: string;
  price: string;
  currency: string;
  converted_price?: number | null;
  converted_currency?: string | null;
  converted_currency_symbol?: string | null;
  duration_in_days: number;
  core_outputs_json: Array<{ name: string; path: string }>;
  product_category: number;
  product_category_name?: string;
  is_free: boolean;
  service_type?: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
  created_at: string;
}

interface ProductCategory {
  id: number;
  name: string;
  short_description: string;
  category_type: string;
  category_type_color?: string;
  long_description: string;
  created_at: string;
}

interface CategoryProductsProps {
  categoryId: number;
  onBack: () => void;
  onProductSelect?: (productId: number) => void;
  selectedCurrency?: string;
}

const CategoryProducts: React.FC<CategoryProductsProps> = ({ categoryId, onBack, onProductSelect, selectedCurrency = 'CHF' }) => {
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategory = useCallback(async () => {
    try {
      const response = await fetch(`/api/product-categories/${categoryId}/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCategory(data);
      }
    } catch (err) {
      console.error('Error fetching category:', err);
    }
  }, [categoryId]);

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Don't pass currency param if it's CHF (default) - backend will use user's preference
      const url = `/api/products/?category=${categoryId}${selectedCurrency && selectedCurrency !== 'CHF' ? `&currency=${selectedCurrency}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      } else {
        setError('Failed to load products');
      }
    } catch (err) {
      setError('An error occurred while loading products');
      console.error('Products fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [categoryId, selectedCurrency]);

  useEffect(() => {
    fetchCategory();
    fetchProducts();
  }, [categoryId, selectedCurrency, fetchCategory, fetchProducts]);

  useEffect(() => {
    const scrollToTop = () => {
      const main = document.querySelector('main.flex-1.overflow-auto');
      if (main instanceof HTMLElement) {
        main.scrollTo({ top: 0, left: 0 });
        return;
      }
      window.scrollTo({ top: 0, left: 0 });
    };
    requestAnimationFrame(scrollToTop);
  }, [categoryId]);

  const handleProductSelect = (productId: number) => {
    if (onProductSelect) {
      onProductSelect(productId);
    }
  };

  const formatPrice = (product: Product, duration: number, isFree: boolean) => {
    if (product.service_type === 'DIY') {
      return 'Included';
    }
    if (isFree) {
      return 'FREE';
    }
    
    // Use converted price if available, otherwise use original
    const displayPrice = product.converted_price ?? parseFloat(product.price);
    const displayCurrency = product.converted_currency_symbol ?? product.currency;
    
    if (displayPrice === 0) {
      return 'Price FREE';
    }
    return `Price from ${displayCurrency} ${displayPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} in ${duration} day${duration !== 1 ? 's' : ''}`;
  };

  // Get category color from API, with fallback to default
  const getCategoryColor = (category: ProductCategory | null): string => {
    if (category?.category_type_color) {
      return category.category_type_color;
    }
    // Fallback to default color if not set in database
    return '#C3D74F';
  };

  const categoryColor = getCategoryColor(category);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-auto">
      {/* Header */}
      <div className="border-b border-gray-200 h-[80px] 2xl:h-[100px] flex items-center px-8 2xl:px-12 bg-white/80 backdrop-blur-sm shadow-sm relative">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="absolute left-8 2xl:left-12 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 2xl:w-6 2xl:h-6" />
          <span className="text-sm 2xl:text-base">Back</span>
        </button>
        <h1 className="text-3xl 2xl:text-4xl font-bold text-black leading-8 2xl:leading-10 mx-auto">
          MARKETPLACE
        </h1>
      </div>

      {/* Section Header */}
      {category && (
        <div className="relative py-12 2xl:py-16">
          <div className="px-8 2xl:px-12 mb-10 2xl:mb-12">
            <div className="flex items-center gap-4 2xl:gap-5 mb-3 2xl:mb-4">
              <div 
                className="w-8 h-8 2xl:w-10 2xl:h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                style={{ backgroundColor: categoryColor }}
              />
              <h2 className="text-3xl 2xl:text-4xl font-semibold text-black leading-8 2xl:leading-10 flex items-center gap-3 2xl:gap-4">
                <div className="w-1.5 h-8 2xl:w-2 2xl:h-10 rounded-full" style={{ backgroundColor: categoryColor }}></div>
                <span className="normal-case">{category ? `${category.category_type.toUpperCase()} / ${category.name}` : 'Products'}</span>
              </h2>
            </div>
            <div className="h-2 2xl:h-3 rounded-full w-full" style={{ backgroundColor: `${categoryColor}20` }}></div>
          </div>

          {/* Description */}
          {category.long_description && (
            <div className="px-8 2xl:px-12 mb-10 2xl:mb-12">
              <p className="text-lg 2xl:text-xl text-black leading-7 2xl:leading-8 whitespace-pre-wrap max-w-[1400px] 2xl:max-w-[1600px]">
                {category.long_description}
              </p>
            </div>
          )}

          {/* Products Grid */}
          {products.length > 0 ? (
            <div className="relative px-8 2xl:px-12 pb-8 2xl:pb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 2xl:gap-12 max-w-[1400px] 2xl:max-w-[1600px] mx-auto justify-items-center">
                {products.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleProductSelect(product.id)}
                    className="group bg-white border-2 border-gray-200 w-full max-w-[420px] 2xl:max-w-[480px] min-h-[320px] 2xl:min-h-[360px] overflow-hidden rounded-xl text-left focus:outline-none transition-all duration-300 hover:scale-[1.02] relative"
                    style={{
                      '--category-color': categoryColor,
                    } as React.CSSProperties & { '--category-color': string }}
                    onMouseEnter={(e) => {
                      const button = e.currentTarget;
                      button.style.borderColor = categoryColor;
                      button.style.boxShadow = `0 20px 25px -5px ${categoryColor}25, 0 10px 10px -5px ${categoryColor}10`;
                      const arrow = button.querySelector('[data-arrow-icon]') as SVGElement;
                      if (arrow) arrow.style.color = categoryColor;
                    }}
                    onMouseLeave={(e) => {
                      const button = e.currentTarget;
                      button.style.borderColor = '';
                      button.style.boxShadow = '';
                      const arrow = button.querySelector('[data-arrow-icon]') as SVGElement;
                      if (arrow) arrow.style.color = '';
                    }}
                  >
                    {/* Colored Header Bar */}
                    <div 
                      className="h-4 2xl:h-5 transition-colors duration-300 rounded-t-xl"
                      style={{ backgroundColor: categoryColor }}
                    ></div>
                    
                    {/* Content Area */}
                    <div className="p-8 2xl:p-10 bg-white h-full flex flex-col w-full relative">
                      {/* Product Name */}
                      <h3 className="text-base 2xl:text-lg font-bold text-black mb-4 2xl:mb-5 leading-6 2xl:leading-7 uppercase tracking-wide break-words w-full">
                        {product.name}
                      </h3>

                      {/* Product Description */}
                      <p 
                        className="text-base 2xl:text-lg text-black leading-7 2xl:leading-8 break-words w-full mb-5 2xl:mb-6 overflow-hidden" 
                        style={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical' as const,
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {product.short_description || product.long_description}
                      </p>

                      {/* Price */}
                      <p className="text-sm 2xl:text-base text-gray-600 leading-6 2xl:leading-7 mb-5 2xl:mb-6">
                        {formatPrice(product, product.duration_in_days, product.is_free)}
                      </p>
                      
                      {/* Arrow Icon - Bottom Right */}
                      <div className="absolute bottom-8 2xl:bottom-10 right-8 2xl:right-10">
                        <ArrowRight 
                          data-arrow-icon
                          className="w-6 h-6 2xl:w-7 2xl:h-7 text-gray-400 transition-colors duration-300" 
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="px-8 2xl:px-12 pb-8 2xl:pb-12">
              <div className="text-center py-12 2xl:py-16">
                <Package className="w-16 h-16 2xl:w-20 2xl:h-20 text-gray-300 mx-auto mb-4 2xl:mb-5" />
                <p className="text-gray-600 text-lg 2xl:text-xl">No products available</p>
                <p className="text-gray-500 text-sm 2xl:text-base mt-2 2xl:mt-3">
                  Products will appear here once they are added to this category
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryProducts;

